/**
 * QHS UnsavedChangesModal
 * -----------------------
 * v3-styled confirmation shown when the user tries to leave a form
 * page with unsaved changes. Sits on top of the shared `Dialog`
 * primitive so dialog plumbing (titlesBox padding, scrollable body,
 * footer separator, escape/click-outside) lives in one place.
 *
 *   <UnsavedChangesModal
 *     open={dirty && pendingNav}
 *     title={unsavedChanges.title}
 *     message={unsavedChanges.orderEdit}
 *     keepEditingLabel={unsavedChanges.keepEditing}
 *     discardLabel={unsavedChanges.leaveAnyway}
 *     onKeepEditing={cancel}
 *     onDiscard={proceed}
 *   />
 *
 * Originally lived in `pages/account-edit/ui/UnsavedChangesModal`.
 * Moved here so order-edit, profile-edit, and any future form page
 * can share the same redesigned dialog instead of falling back to
 * the legacy MUI alert.
 */
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { styled } from '../styled';

const Body = styled('div')({
  padding: '36px 40px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const IconBadge = styled('div')({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'var(--qhs-st-high-bg)',
  color: 'var(--qhs-st-high-tx)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
  '& svg': { width: 22, height: 22 },
});

const Title = styled('h3')({
  margin: '0 0 12px',
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.4px',
  lineHeight: 1.2,
});

const Message = styled('p')({
  margin: 0,
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--qhs-text-2)',
});

const FooterRow = styled('div')({
  padding: '16px 32px',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 8,
});

const WarningIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);

const DEFAULT_LABELS = {
  title: 'Unsaved changes',
  message: 'You have unsaved changes. Leave anyway?',
  keepEditing: 'Keep editing',
  discard: 'Leave anyway',
};

const UnsavedChangesModal = ({
  open,
  onDiscard,
  onKeepEditing,
  title,
  message,
  discardLabel,
  keepEditingLabel,
}) => (
  <Dialog
    open={open}
    onClose={onKeepEditing}
    ariaLabel={title || DEFAULT_LABELS.title}
    footer={
      <FooterRow>
        <Button variant='ghost' onClick={onKeepEditing}>
          {keepEditingLabel || DEFAULT_LABELS.keepEditing}
        </Button>
        <Button variant='danger' onClick={onDiscard}>
          {discardLabel || DEFAULT_LABELS.discard}
        </Button>
      </FooterRow>
    }
  >
    <Body>
      <IconBadge>
        <WarningIcon />
      </IconBadge>
      <Title>{title || DEFAULT_LABELS.title}</Title>
      <Message>{message || DEFAULT_LABELS.message}</Message>
    </Body>
  </Dialog>
);

export default UnsavedChangesModal;
